import { useEffect, useState } from "react";
import { Enhancement, ExperienceSummaryModel } from "../../teamExperiences/models/ExperienceSummaryModel";
import { sanitizeHtml as sanitizeGlobalHtml } from "../../../helpers/sanitizeHtml";

type Props = {
  experience: ExperienceSummaryModel;
};

export function SectionInfoTabs({ experience }: Props) {
    const [tabSelected, setTabSelected] = useState(1);
    const [more, setMore] = useState(false);
    const [more2, setMore2] = useState(false);

    const CHAR_LIMIT = 350;

    const getCurrentText = () => {
        switch (tabSelected) {
        case 1:
            return experience?.itinerary || "";
        case 2:
            return experience?.whatIsIncluded || "";
        case 3:
            return experience?.importantInformationGuest || "";
        default:
            return "";
        }
    };

    const getPlainText = (html: string) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };

    const sanitizeHtml = (html: string) => sanitizeGlobalHtml(html) || 'Pending information';

    const hasDescription = !!experience?.itinerary && getPlainText(experience.itinerary).trim().length > 0;
    const hasIncluded = !!experience?.whatIsIncluded && getPlainText(experience.whatIsIncluded).trim().length > 0;
    const hasOptional = (experience.enhancements ?? []).length > 0;
    const hasImportantInfo = !!experience?.importantInformationGuest && getPlainText(experience.importantInformationGuest).trim().length > 0;
    const hasIdealAudience = !!experience?.idealAudience && getPlainText(experience.idealAudience).trim().length > 0;

    const showTab1 = hasDescription;
    const showTab2 = hasIncluded || hasOptional;
    const showTab3 = hasImportantInfo || hasIdealAudience;

    useEffect(() => {
        setMore(false); // reset when tab changes
        if (tabSelected === 1 && !showTab1) {
            if (showTab2) setTabSelected(2);
            else if (showTab3) setTabSelected(3);
        } else if (tabSelected === 2 && !showTab2) {
            if (showTab1) setTabSelected(1);
            else if (showTab3) setTabSelected(3);
        } else if (tabSelected === 3 && !showTab3) {
            if (showTab1) setTabSelected(1);
            else if (showTab2) setTabSelected(2);
        }
    }, [tabSelected, experience, showTab1, showTab2, showTab3]);

    const text = getCurrentText();
    const biography = sanitizeHtml(text);
    const idealAudience = sanitizeHtml(experience.idealAudience || 'Pending information');
    const showButton = getPlainText(text).length > CHAR_LIMIT;

    const showButton2 = getPlainText(experience.idealAudience || '').length > CHAR_LIMIT;

    const renderTab1 = () => {
        return (<div className="w-100 d-flex flex-md-row flex-column gap-100">
            {hasDescription && (
                <div className={`experience-preview-tab-content w-75`}>
                    <div 
                        className={`experience-preview-description-text ${(!more && showButton) ? 'experience-preview-description-text--collapsed' : ''}`} 
                        dangerouslySetInnerHTML={{ __html: biography }} 
                    />
                    {showButton && (
                        <button className="experience-preview-readmore" onClick={() => setMore(!more)}>
                            Read {more ? "less" : "more"}
                        </button>)}
                </div>
            )}
        </div>)
    }

    const renderTab2 = () => {
        return (<div className="w-100 d-flex flex-md-row flex-column gap-100">
            {hasIncluded && (
                <div className={`experience-preview-tab-content ${hasOptional ? 'w-50' : 'w-100'}`}>
                    <div className="experience-preview-included-title">Whats Included</div>
                    <div 
                        className={`experience-preview-description-text ${(!more && showButton) ? 'experience-preview-description-text--collapsed' : ''}`} 
                        dangerouslySetInnerHTML={{ __html: biography }} 
                    />
                    {showButton && (
                        <button className="experience-preview-readmore" onClick={() => setMore(!more)}>
                            Read {more ? "less" : "more"}
                        </button>)}
                </div>
            )}
            {hasOptional && (
                <div className={`experience-preview-tab-content ${hasIncluded ? 'w-50' : 'w-100'}`}>
                    <div className="experience-preview-optional-wrapper w-100">
                        <div className="experience-preview-optional-title">Optional</div>
                            {(experience.enhancements ?? []).map((item:Enhancement,index:number)=>(
                                <div key={`optional-${index}`} className="experience-preview-optional-card">
                                    <div className="experience-preview-optional-card-title">{item.name}</div>
                                    <div className="experience-preview-optional-card-price">${item.price} / {experience?.priceType === 2 || (experience as any)?.price_type === 2 ? 'group' : 'guest'}</div>
                                </div>
                            ))}
                    </div>
                </div>
            )}
        </div>);
    }

    const renderTab3 = () => {
        return (<div className="w-100 d-flex flex-md-row flex-column gap-100">
            {hasImportantInfo && (
                <div className={`experience-preview-tab-content`} style={{ flex: '1 0 0' }}>
                    <div className="experience-preview-included-title">Guest Preparation & Additional Notes</div>
                    <div 
                        className={`experience-preview-description-text ${(!more && showButton) ? 'experience-preview-description-text--collapsed' : ''}`} 
                        dangerouslySetInnerHTML={{ __html: biography }} 
                    />
                    {showButton && (
                        <button className="experience-preview-readmore" onClick={() => setMore(!more)}>
                            Read {more ? "less" : "more"}
                        </button>)}
                </div>
            )}
            {hasIdealAudience && (
                <div className={`experience-preview-tab-content`} style={{ flex: '1 0 0' }}>
                    <div className="experience-preview-optional-wrapper w-100">
                        <div className="experience-preview-optional-title">ideal for</div>

                        <div 
                            className={`experience-preview-description-text ${(!more2 && showButton2) ? 'experience-preview-description-text--collapsed' : ''}`} 
                            dangerouslySetInnerHTML={{ __html: idealAudience }} 
                        />
                        {showButton2 && (
                        <button className="experience-preview-readmore" onClick={() => setMore2(!more2)}>
                            Read {more2 ? "less" : "more"}
                        </button>)}
                    </div>
                </div>
            )}
        </div>);
    }

    return (<div className="experience-preview-tabs-section">
        <style>{`
            .experience-preview-description-text,
            .experience-preview-description-text * {
                color: #ffffff !important;
            }
        `}</style>
        <div className="experience-preview-tabs">
            {showTab1 && (
                <div className={`experience-preview-tab ${tabSelected == 1 ? 'experience-preview-tab--active' : ''}`} onClick={()=>setTabSelected(1)}>
                    <span>Description</span>
                </div>
            )}

            {showTab2 && (
                <div className={`experience-preview-tab ${tabSelected == 2 ? 'experience-preview-tab--active' : ''}`} onClick={()=>setTabSelected(2)}>
                    <span>Included / Optional</span>
                </div>
            )}

            {showTab3 && (
                <div className={`experience-preview-tab ${tabSelected == 3 ? 'experience-preview-tab--active' : ''}`} onClick={()=>setTabSelected(3)}>
                    <span>Additional info</span>
                </div>
            )}
        </div>

        {/* Content */}
        {tabSelected === 1 && showTab1 && (renderTab1())}
        {tabSelected === 2 && showTab2 && (renderTab2())}
        {tabSelected === 3 && showTab3 && (renderTab3())}
    </div>);
}
